import { motion } from "framer-motion";
import { ScrollReveal } from "@/components/ScrollReveal";
import { CONTACT_DATA, TERMS_OF_SERVICE_DATA } from "@/data/siteData";
import { Link } from "react-router-dom";

const TermsSection = ({ title, children }) => (
  <div className="space-y-3">
    <h2 className="font-['Outfit'] text-xl md:text-2xl font-semibold tracking-tight text-[#0A0A0A]">
      {title}
    </h2>
    <div className="text-sm md:text-base text-[#475569] leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function TermsOfService() {
  return (
    <div>
      <section className="relative py-24 md:py-32 overflow-hidden mesh-bg" data-testid="terms-hero">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#097DC3]/8 text-[#097DC3] text-sm font-semibold uppercase tracking-wider mb-6">
              {TERMS_OF_SERVICE_DATA.hero.tagline}
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#0A0A0A] mb-6">
              {TERMS_OF_SERVICE_DATA.hero.titlePrefix} <span className="text-[#097DC3]">{TERMS_OF_SERVICE_DATA.hero.titleHighlight}</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#475569] max-w-2xl">
              {TERMS_OF_SERVICE_DATA.hero.subtitle}
            </p>
            <p className="text-sm text-slate-500 mt-6">Last updated: {TERMS_OF_SERVICE_DATA.lastUpdated}</p>
          </motion.div>
        </div>
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-[#8CDDF6]/15 blur-3xl" />
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="terms-content">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="space-y-12">
            <ScrollReveal>
              <TermsSection title="1) Acceptance of Terms">
                <p>
                  By accessing or using this website, you agree to these Terms of Service. If you do not agree, do not use the website.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.05}>
              <TermsSection title="2) About Our Services">
                <p>
                  Uproot Innovation provides information about our services (including software development, student empowerment tracks, mentorship programs, events, and partnerships) and offers ways to contact us.
                  Some programs or offerings may be subject to additional terms communicated at the time of enrollment or engagement.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <TermsSection title="3) Permitted Use">
                <p>You agree not to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Use the website for unlawful, harmful, or abusive purposes.</li>
                  <li>Attempt to gain unauthorized access to systems, accounts, or data.</li>
                  <li>Interfere with or disrupt the website’s availability or security.</li>
                  <li>Submit false, misleading, or infringing content through forms.</li>
                </ul>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <TermsSection title="4) Intellectual Property">
                <p>
                  The website content (including text, graphics, branding, design, and code) is owned by or licensed to Uproot Innovation and is protected by applicable intellectual property laws.
                  You may view and use the website for personal or internal business purposes, but you may not copy, reproduce, distribute, or create derivative works without written permission.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <TermsSection title="5) User Submissions (Inquiries & Testimonials)">
                <p>
                  If you submit information through forms (including inquiries or testimonials), you represent that you have the right to share that information.
                  Testimonials and feedback may be reviewed and displayed on the website only when approved.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.25}>
              <TermsSection title="6) Third-Party Services and Links">
                <p>
                  The website may use third-party services (such as hosting, databases, analytics, and form delivery) and may contain links to third-party websites.
                  We are not responsible for third-party content, policies, or practices.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <TermsSection title="7) Disclaimers">
                <p>
                  The website is provided on an “as is” and “as available” basis. While we aim for accuracy, we do not warrant that the website will be error-free, uninterrupted, or that content will always be complete or current.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.35}>
              <TermsSection title="8) Limitation of Liability">
                <p>
                  To the maximum extent permitted by law, Uproot Innovation will not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of (or inability to use) the website.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <TermsSection title="9) Indemnity">
                <p>
                  You agree to defend, indemnify, and hold harmless Uproot Innovation from and against claims, liabilities, damages, losses, and expenses arising out of your misuse of the website or violation of these Terms.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.45}>
              <TermsSection title="10) Privacy">
                <p>
                  Your use of the website is also governed by our Privacy Policy.
                  See <Link to="/privacy" className="text-[#097DC3] hover:underline">Privacy Policy</Link> for details.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.5}>
              <TermsSection title="11) Changes to These Terms">
                <p>
                  We may update these Terms from time to time. Changes will be posted on this page with an updated “Last updated” date.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.55}>
              <TermsSection title="12) Governing Law">
                <p>
                  These Terms are governed by the laws of India. Courts having jurisdiction in Chennai, Tamil Nadu shall have exclusive jurisdiction over disputes arising from these Terms or your use of the website.
                </p>
              </TermsSection>
            </ScrollReveal>

            <ScrollReveal delay={0.6}>
              <TermsSection title="13) Contact Us">
                <p>If you have questions about these Terms, contact us at:</p>
                <div className="rounded-2xl border border-slate-100 bg-[#F8FAFC] p-5">
                  <p className="text-sm md:text-base text-[#0A0A0A] font-medium">Uproot Innovation</p>
                  <p className="text-sm md:text-base">Email: {CONTACT_DATA.office.email}</p>
                  <p className="text-sm md:text-base">Phone: {CONTACT_DATA.office.phone}</p>
                </div>
              </TermsSection>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
